import React from 'react';
import { Check } from 'lucide-react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';

interface Feature {
  name: string;
  planniFi: boolean;
  otherPlatforms: boolean;
  excel: boolean;
}

const features: Feature[] = [
  {
    name: "Data visualization",
    planniFi: true,
    otherPlatforms: true,
    excel: true
  },
  {
    name: "Real-time data synchronization",
    planniFi: true,
    otherPlatforms: true,
    excel: false
  },
  {
    name: "Automated reporting",
    planniFi: true,
    otherPlatforms: true,
    excel: false
  },
  {
    name: "Custom dashboards",
    planniFi: true,
    otherPlatforms: true,
    excel: false
  },
  {
    name: "Budgeting tools",
    planniFi: true,
    otherPlatforms: false,
    excel: false
  },
  {
    name: "Spend pacing",
    planniFi: true,
    otherPlatforms: false,
    excel: false
  },
  {
    name: "Spend variance analysis",
    planniFi: true,
    otherPlatforms: false,
    excel: false
  },
].sort((a, b) => {
  const aCount = Number(a.planniFi) + Number(a.otherPlatforms) + Number(a.excel);
  const bCount = Number(b.planniFi) + Number(b.otherPlatforms) + Number(b.excel);
  return bCount - aCount;
});

const FeatureComparisonChart = () => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <div className="max-w-7xl mx-auto mt-24">
      <Card className="rounded-[20px] overflow-hidden border-0 shadow-none bg-[#0F5E48]">
        <div className="px-4 md:px-8">
          <h2 className="text-3xl md:text-5xl font-charter mb-4 md:mb-8 text-center text-white pt-8">
            Why settle for less?<br />
            See how we stack up.
          </h2>
          
          <div 
            ref={ref}
            className={`overflow-x-auto transition-all duration-1000 bg-white rounded-lg ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <table className="w-full relative">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 md:py-4 px-2 md:px-6 text-center font-charter text-[10px] md:text-xl w-[40%] text-gray-800">Features</th>
                  <th className="py-2 md:py-4 px-2 md:px-6 text-center font-charter text-[10px] md:text-xl w-[20%] border-l border-gray-200">
                    <img 
                      src="/lovable-uploads/ebba3bd6-6f73-4dd8-af55-81ad7b9c4ff9.png" 
                      alt="planniFi" 
                      className="h-3 md:h-6 mx-auto"
                    />
                  </th>
                  <th className="py-2 md:py-4 px-2 md:px-6 text-center font-charter text-[10px] md:text-xl w-[20%] border-l border-gray-200 text-gray-800">Other Platforms</th>
                  <th className="py-2 md:py-4 px-2 md:px-6 text-center font-charter text-[10px] md:text-xl w-[20%] border-l border-gray-200 text-gray-800">Excel</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr 
                    key={feature.name}
                    className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                  >
                    <td className="py-1 md:py-4 px-2 md:px-6 w-[40%] text-gray-800 text-[10px] md:text-base">{feature.name}</td>
                    <td className="py-1 md:py-4 px-2 md:px-6 text-center w-[20%] border-l border-gray-200">
                      {feature.planniFi && (
                        <Check className="inline-block text-green-500 h-3 w-3 md:h-6 md:w-6" />
                      )}
                    </td>
                    <td className="py-1 md:py-4 px-2 md:px-6 text-center w-[20%] border-l border-gray-200">
                      {feature.otherPlatforms && (
                        <Check className="inline-block text-green-500 h-3 w-3 md:h-6 md:w-6" />
                      )}
                    </td>
                    <td className="py-1 md:py-4 px-2 md:px-6 text-center w-[20%] border-l border-gray-200">
                      {feature.excel && (
                        <Check className="inline-block text-green-500 h-3 w-3 md:h-6 md:w-6" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pb-8"></div>
        </div>
      </Card>
    </div>
  );
};

export default FeatureComparisonChart;
